// Round-trip helpers between backend ISO strings (with offset) and the
// `<input type="datetime-local">` format "YYYY-MM-DDTHH:mm". All conversions
// assume the wall-clock timezone Europe/Zurich (the editorial timezone).
//
// Why not date-fns-tz? We already avoid extra deps on the web bundle and
// `Intl.DateTimeFormat` plus `Date.UTC` give us everything we need.

const TIMEZONE = "Europe/Zurich";

interface TzParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * Wall-clock parts of a UTC instant as rendered in the given timezone.
 * Uses Intl.DateTimeFormat so DST is handled by the platform.
 */
function getPartsInTz(date: Date, tz: string): TzParts {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = fmt.formatToParts(date);
  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== "literal") map[p.type] = p.value;
  }
  // "hour" can come back as "24" for midnight in some locales; normalize.
  const rawHour = map.hour ?? "0";
  const hour = rawHour === "24" ? 0 : Number(rawHour);
  return {
    year: Number(map.year ?? "0"),
    month: Number(map.month ?? "0"),
    day: Number(map.day ?? "0"),
    hour,
    minute: Number(map.minute ?? "0"),
    second: Number(map.second ?? "0"),
  };
}

/**
 * Offset of `tz` at the given UTC moment, in minutes (positive east of UTC).
 * For Europe/Zurich this is +60 in winter, +120 in summer.
 */
function getTzOffsetMinutes(utcMs: number, tz: string): number {
  const p = getPartsInTz(new Date(utcMs), tz);
  const pretendUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  return Math.round((pretendUtc - utcMs) / 60000);
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * Convert a backend ISO string (any offset) to the value expected by an
 * `<input type="datetime-local">` interpreted in Europe/Zurich.
 *
 * Example: "2026-04-28T15:00:00Z" -> "2026-04-28T17:00" (summer, CEST)
 */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = getPartsInTz(d, TIMEZONE);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}T${pad2(p.hour)}:${pad2(p.minute)}`;
}

/**
 * Convert a `<input type="datetime-local">` value (interpreted as Europe/Zurich
 * wall-clock) back to a backend-compatible ISO string with offset.
 *
 * Example: "2026-04-28T17:00" -> "2026-04-28T17:00:00+02:00"
 * Returns "" if `local` is not in the expected format.
 */
export function localInputToIso(local: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (!m) return "";
  const [, Y, Mo, D, H, Mi] = m as unknown as [string, string, string, string, string, string];
  const year = Number(Y);
  const month = Number(Mo);
  const day = Number(D);
  const hour = Number(H);
  const minute = Number(Mi);

  // 1st guess: treat parts as UTC and look up the offset there.
  let utcGuess = Date.UTC(year, month - 1, day, hour, minute, 0);
  let off = getTzOffsetMinutes(utcGuess, TIMEZONE);
  // Correct the guess by subtracting the offset.
  let utcActual = utcGuess - off * 60000;
  // Around DST transitions the offset at utcActual might differ from the
  // offset at utcGuess; run one more iteration to converge.
  const off2 = getTzOffsetMinutes(utcActual, TIMEZONE);
  if (off2 !== off) {
    off = off2;
    utcActual = utcGuess - off * 60000;
  }

  const sign = off >= 0 ? "+" : "-";
  const absOff = Math.abs(off);
  const offH = pad2(Math.floor(absOff / 60));
  const offM = pad2(absOff % 60);
  return `${Y}-${Mo}-${D}T${H}:${Mi}:00${sign}${offH}:${offM}`;
}
